import { LayoutTemplate, Library } from "lucide-react";
import { useState, type FC } from "react";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

export type LibrarySource = {
  /** Record-mapped questions, grouped by topic. */
  bank: { id: string; label: string; questions: OutgoingIntakeFormNode[] }[];
  /** Whole forms (USCIS templates) to add at once. */
  forms: { id: string; label: string; meta: string }[];
  /** Load the bank's questions; called when the picker opens. */
  ensureLoaded: () => void;
};

type Props = {
  source: LibrarySource;
  onAddQuestion: (node: OutgoingIntakeFormNode) => void;
  onAddForm: (formId: string) => void;
};

const labelOf = (n: OutgoingIntakeFormNode) =>
  String(n.prompt ?? n.title ?? n.node_id);

/** "From Library": any record-mapped question, or a whole USCIS form. */
export const LibraryPicker: FC<Props> = ({
  source,
  onAddQuestion,
  onAddForm
}) => {
  const [open, setOpen] = useState(false);
  const pick = (fn: () => void) => {
    fn();
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) source.ensureLoaded();
        setOpen(o);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" size="xs">
          <Library className="size-3" /> From Library
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-95 p-0">
        <Command>
          <CommandInput placeholder="Search questions and forms…" />
          <CommandList className="max-h-80">
            <CommandEmpty>Nothing matches.</CommandEmpty>
            {source.bank.map((group) => (
              <CommandGroup key={group.id} heading={group.label}>
                {group.questions.map((q) => (
                  <CommandItem
                    key={q.node_id}
                    value={`${group.label} ${labelOf(q)}`}
                    onSelect={() => pick(() => onAddQuestion(q))}
                  >
                    <span className="truncate">{labelOf(q)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {source.forms.length ? (
              <>
                <CommandSeparator />
                <CommandGroup heading="USCIS Forms">
                  {source.forms.map((f) => (
                    <CommandItem
                      key={f.id}
                      value={`${f.label} ${f.meta}`}
                      onSelect={() => pick(() => onAddForm(f.id))}
                    >
                      <LayoutTemplate className="size-3.5" />
                      <span className="truncate">{f.label}</span>
                      <span className="ml-auto text-sm text-fg-3">
                        {f.meta}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
